import React from "react";
import ModelItem from "./ModelItem/ModelItem.tsx";
import { OverlayWrapper } from "../../../../../OverlayWrapper.tsx";
import { useAppStore } from "../../../../../../GlobalStore/GlobalStore.ts";

interface ModelOption {
    provider: string;
    models: {
        name: string;
        isPremium: boolean;
    }[];
}

interface ModelProviderProps {
    option: ModelOption;
    handleModelSelect: (model: string) => void;
    setIsOpen: (isOpen: boolean) => void;
}

const ModelProvider: React.FC<ModelProviderProps> = ({ option, handleModelSelect, setIsOpen }) => {
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const userObj = useAppStore((state: any) => state.userObj);

    // Check if API key mode is enabled and if provider has API key
    const isApiKeyMode = settingsObj.apiKeyOnly;
    const hasApiKey = () => {
        switch (option.provider) {
            case 'Anthropic':
                return environmentObj.anthropicApiKey !== null && environmentObj.anthropicApiKey !== '';
            case 'OpenAI':
                return environmentObj.openAiApiKey !== null && environmentObj.openAiApiKey !== '';
            case 'xAI':
                return environmentObj.xAiApiKey !== null && environmentObj.xAiApiKey !== '';
            case 'Google':
                return environmentObj.geminiApiKey !== null && environmentObj.geminiApiKey !== '';
            default:
                return false;
        }
    };

    const shouldShowOverlay = isApiKeyMode && !hasApiKey();

    // Filter models based on usage-based pricing and API key mode
    const modelsToDisplay = option.models.filter(model => {
        // If API key mode is enabled, show all models (premium or not)
        if (isApiKeyMode) {
            return true;
        }
        // If usage-based pricing is enabled, show all models
        if (userObj.hasUsageBasedEnabled || userObj.monthlyFreeMessages > 0) {
            return true;
        }
        // If usage-based pricing is disabled, only show non-premium models
        return !model.isPremium;
    });

    // If no models are left after filtering, don't render the provider at all
    if (modelsToDisplay.length === 0) {
        return null;
    }

    const providerContent = (
        <div className={`border-t border-neutral-300 dark:border-neutral-700`}>
            <div className="px-4 py-2 font-semibold text-black dark:text-white text-center">
                {option.provider}
            </div>
            {modelsToDisplay.map((model) => (
                <ModelItem
                    key={model.name}
                    modelName={model.name}
                    isPremium={model.isPremium}
                    handleModelSelect={handleModelSelect}
                    setIsOpen={setIsOpen}
                />
            ))}
        </div>
    );

    return shouldShowOverlay ? (
        <OverlayWrapper type="apiKeyModeNoKey" toolTipDirection="top-left">
            {providerContent}
        </OverlayWrapper>
    ) : providerContent;
};



export default ModelProvider;